'use client'

import { useEffect, useMemo, useState } from 'react'
import { getIdToken } from 'firebase/auth'
import { useTranslation } from 'react-i18next'
import { ClipLoader } from 'react-spinners'
import { FaCheck, FaUsers } from 'react-icons/fa'
import { useAuth } from '../contexts/AuthContext'

const MAX_MEMBERS = 2
const FALLBACK_AVATAR =
  'https://gravatar.com/avatar/f5862edde16dd6f7a1e30905b4096241?s=200&d=wavatar&r=x'

// iMessage/Messenger-style group creator: tap up to 2 of your existing
// characters, choose which scenario the group runs on (a character's own
// scenario — they open the chat — or a custom one where you start), optionally
// name it, and create. Rendered as the content of the shared <Modal>.
const CreateGroup = ({ characters = [], onCreate, closeModal }) => {
  const { user } = useAuth()
  const { t } = useTranslation()

  const [selected, setSelected] = useState([])
  // '' (unset) | characterId (use their scenario, they open) | 'custom'
  const [scenarioChoice, setScenarioChoice] = useState('')
  const [customScenario, setCustomScenario] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const byId = useMemo(() => {
    const map = {}
    characters.forEach((c) => (map[c.id] = c))
    return map
  }, [characters])

  const avatarOf = (c) => (c?.imageUrl || [])[0] || FALLBACK_AVATAR
  const scenarioOf = (id) => (byId[id]?.scenario || '').trim()

  const toggle = (id) => {
    setError('')
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= MAX_MEMBERS) return prev
      return [...prev, id]
    })
  }

  // Keep the scenario choice valid as the selection changes: default to the
  // first selected character that has a scenario, otherwise "custom".
  useEffect(() => {
    const stillValid =
      scenarioChoice === 'custom' ||
      (selected.includes(scenarioChoice) && scenarioOf(scenarioChoice))
    if (stillValid) return
    const firstWithScenario = selected.find((id) => scenarioOf(id))
    setScenarioChoice(firstWithScenario || (selected.length ? 'custom' : ''))
    // Reruns on selection change only; scenarioOf/byId are derived from props.
  }, [selected])

  const scenarioCharacters = selected
    .map((id) => byId[id])
    .filter((c) => c && scenarioOf(c.id))

  const createGroup = async () => {
    if (!user || saving) return
    if (selected.length < 1) {
      setError(
        t('groupPickAtLeastOne', {
          defaultValue: 'Pick at least one character.',
        }),
      )
      return
    }

    const useCustom =
      scenarioChoice === 'custom' || !selected.includes(scenarioChoice)
    const scenario = useCustom
      ? customScenario.trim()
      : scenarioOf(scenarioChoice)
    const initiatorId = useCustom ? null : scenarioChoice

    setSaving(true)
    setError('')
    try {
      const idToken = await getIdToken(user)
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/create_group`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            participant_ids: selected,
            scenario,
            initiator_id: initiatorId,
          }),
        },
      )
      const data = await res.json().catch(() => ({}))
      if (res.ok && data?.group) {
        onCreate?.(data.group)
        closeModal?.()
      } else {
        setError(
          data?.error ||
            t('somethingWentWrong', {
              defaultValue: 'Something went wrong. Please try again.',
            }),
        )
      }
    } catch (e) {
      console.error('Failed to create group:', e)
      setError(
        t('somethingWentWrong', {
          defaultValue: 'Something went wrong. Please try again.',
        }),
      )
    } finally {
      setSaving(false)
    }
  }

  const choiceCardClass = (active) =>
    `w-full text-left rounded-xl border px-3 py-2 transition-colors duration-200 ${
      active
        ? 'border-[var(--main-bg-color)] bg-[var(--foreground-bg-color)]'
        : 'border-[var(--secondary-bg-color)] hover:bg-[var(--foreground-bg-color)]/60'
    }`

  return (
    <div className="w-full max-w-xl mx-auto">
      <p className="text-sm text-gray-400 leading-relaxed mb-4">
        {t('groupIntro', {
          defaultValue:
            'Start a group chat — pick up to 2 of your characters and talk to them together.',
        })}
      </p>

      {/* Selected members as chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selected.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1.5 bg-[var(--main-bg-color)] text-white text-xs font-semibold pl-1 pr-2.5 py-1 rounded-full"
            >
              <img
                src={avatarOf(byId[id])}
                alt=""
                className="w-5 h-5 rounded-full object-cover object-top"
              />
              {byId[id]?.name || 'Anon'}
            </span>
          ))}
        </div>
      )}

      {/* Character picker */}
      <div className="max-h-[38vh] overflow-y-auto rounded-xl border border-[var(--secondary-bg-color)] divide-y divide-[var(--secondary-bg-color)]">
        {characters.length === 0 ? (
          <div className="p-4 text-sm text-gray-400 text-center">
            {t('groupNoCharacters', {
              defaultValue:
                'You need a character first. Create or discover one, then start a group.',
            })}
          </div>
        ) : (
          characters.map((c) => {
            const isSel = selected.includes(c.id)
            const disabled = !isSel && selected.length >= MAX_MEMBERS
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                disabled={disabled}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                  isSel
                    ? 'bg-[var(--foreground-bg-color)]'
                    : 'hover:bg-[var(--foreground-bg-color)]/60'
                } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <img
                  src={avatarOf(c)}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover object-top shrink-0"
                />
                <span className="flex-1 min-w-0 truncate text-white font-medium">
                  {c.name || 'Anon'}
                </span>
                <span
                  className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                    isSel
                      ? 'bg-[var(--main-bg-color)] border-[var(--main-bg-color)]'
                      : 'border-gray-500'
                  }`}
                >
                  {isSel && <FaCheck size={10} className="text-white" />}
                </span>
              </button>
            )
          })
        )}
      </div>

      <p className="mt-2 text-xs text-gray-500">
        {selected.length}/{MAX_MEMBERS}{' '}
        {t('selected', { defaultValue: 'selected' })}
      </p>
      <div className="mb-4" />

      {/* Scenario picker */}
      {selected.length > 0 && (
        <div className="mb-4">
          <label className="block text-white text-sm font-semibold mb-2">
            {t('groupScenarioLabel', { defaultValue: 'Scenario' })}
          </label>
          <div className="space-y-2">
            {scenarioCharacters.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setScenarioChoice(c.id)}
                className={choiceCardClass(scenarioChoice === c.id)}
              >
                <div className="text-white text-sm font-medium mb-0.5">
                  {c.name}
                  <span className="ml-1.5 text-xs text-gray-400 font-normal">
                    {t('opensTheChat', { defaultValue: '· opens the chat' })}
                  </span>
                </div>
                <div className="text-xs text-gray-400 line-clamp-2">
                  {c.scenario}
                </div>
              </button>
            ))}

            <button
              type="button"
              onClick={() => setScenarioChoice('custom')}
              className={choiceCardClass(scenarioChoice === 'custom')}
            >
              <div className="text-white text-sm font-medium">
                {t('customScenario', { defaultValue: 'Custom scenario' })}
                <span className="ml-1.5 text-xs text-gray-400 font-normal">
                  {t('youStart', { defaultValue: '· you start' })}
                </span>
              </div>
            </button>

            {scenarioChoice === 'custom' && (
              <textarea
                value={customScenario}
                onChange={(e) => setCustomScenario(e.target.value)}
                maxLength={300}
                placeholder={t('groupScenarioPlaceholder', {
                  defaultValue: 'Describe the situation the group is in...',
                })}
                className="border border-gray-600 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none h-16 resize-none"
              />
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-center text-red-400 my-3">{error}</p>
      )}

      <button
        onClick={createGroup}
        disabled={saving || selected.length < 1}
        className="mt-1 bg-[var(--main-bg-color)] text-white rounded-full py-2 px-4 w-full font-semibold flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-50"
      >
        {saving ? <ClipLoader size={16} color="#fff" /> : <FaUsers size={14} />}
        {t('createGroup', { defaultValue: 'Create group' })}
      </button>
    </div>
  )
}

export default CreateGroup
